import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Box, Chip, Stack, Typography } from '@mui/material';
import ArcadeGameShell from '../ArcadeGameShell';
import { useArcadeAgentSession } from '../useArcadeAgentSession';
import { ManualPlayChrome, useManualPlay, usePlayKeyboard } from '../manualPlay';
import { GAME_BOILERPLATE } from './AGENT_BOILERPLATE';
import { ARCADE } from '../../../utils/config';
import { useManualRunRecorder } from '../useManualRunRecorder';
import {
    createBlackjackSession,
    deleteBlackjackSession,
    moveBlackjackSession,
    WIN_POINTS,
    BLACKJACK_POINTS,
    PUSH_POINTS,
    DEFAULT_BET,
    INITIAL_BANKROLL,
    roundScoreGain,
} from './blackjackGmClient';
import { useTransientMessage } from '../useTransientMessage';
import { gmStartErrorMessage } from '../gmLease';
import { COLOR, GAME_ACCENT } from '../ui/tokens';

const GAME_ID = 'blackjack';
const { UPLOAD_URL } = ARCADE.blackjack;
const ACCENT = GAME_ACCENT.blackjack;
const HINT = 'H hit · S stand · D double · N new round · [ ] adjust bet · Esc pause';

export { WIN_POINTS, BLACKJACK_POINTS, PUSH_POINTS, roundScoreGain };

const CardChip = ({ card, highlight }) => (
    <Box sx={{
        px: 1.5,
        py: 1,
        borderRadius: 1,
        background: highlight ? 'linear-gradient(135deg, #00B4D8, #90E0EF)' : 'rgba(255,255,255,0.1)',
        border: '1px solid rgba(255,255,255,0.2)',
        color: 'white',
        fontWeight: 'bold',
        fontSize: '1rem',
    }}
    >
        {card}
    </Box>
);

const handValue = (hand) => {
    let total = 0;
    let aces = 0;
    (hand || []).forEach((card) => {
        const rank = card.startsWith('10') ? '10' : card[0];
        if (rank === 'A') { total += 11; aces += 1; }
        else if (['K', 'Q', 'J'].includes(rank)) total += 10;
        else total += Number(rank);
    });
    while (total > 21 && aces > 0) { total -= 10; aces -= 1; }
    return total;
};

const Blackjack = () => {
    const play = useManualPlay();
    const sessionIdRef = useRef(null);
    const betRef = useRef(DEFAULT_BET);
    const [playerHand, setPlayerHand] = useState([]);
    const [dealerHand, setDealerHand] = useState([]);
    const [bankroll, setBankroll] = useState(INITIAL_BANKROLL);
    const [bet, setBet] = useState(DEFAULT_BET);
    const [score, setScore] = useState(0);
    const [roundActive, setRoundActive] = useState(false);
    const [totalRounds, setTotalRounds] = useState(0);
    const [isGameOver, setIsGameOver] = useState(false);
    const [message, setMessage] = useTransientMessage();
    const [wins, setWins] = useState(0);
    const [losses, setLosses] = useState(0);
    const [busy, setBusy] = useState(false);

    const session = useArcadeAgentSession({ gameId: GAME_ID, uploadUrl: UPLOAD_URL });
    const recorder = useManualRunRecorder({ gameId: GAME_ID, session });

    const applyGm = useCallback((data) => {
        setPlayerHand(Array.isArray(data.player_hand) ? data.player_hand.slice() : []);
        setDealerHand(Array.isArray(data.dealer_hand) ? data.dealer_hand.slice() : []);
        if (typeof data.bankroll === 'number') setBankroll(data.bankroll);
        if (typeof data.current_bet === 'number') {
            setBet(data.current_bet);
            betRef.current = data.current_bet;
        }
        if (typeof data.score === 'number') setScore(data.score);
        setRoundActive(Boolean(data.round_active));
        if (typeof data.total_rounds === 'number') setTotalRounds(data.total_rounds);
        setIsGameOver(Boolean(data.is_game_over));
        if (typeof data.wins === 'number') setWins(data.wins);
        if (typeof data.losses === 'number') setLosses(data.losses);
        setMessage(data.message || '');
        return {
            over: Boolean(data.is_game_over),
            score: typeof data.score === 'number' ? data.score : 0,
            bankroll: typeof data.bankroll === 'number' ? data.bankroll : 0,
            wins: typeof data.wins === 'number' ? data.wins : 0,
            losses: typeof data.losses === 'number' ? data.losses : 0,
            outcome: data.round_outcome || null,
        };
    }, [setMessage]);

    const maybeFinalize = useCallback((meta) => {
        if (!meta.over) return;
        play.markOver();
        recorder.finalizeAndUpload({
            score: meta.score,
            extra: {
                bankroll: meta.bankroll,
                wins: meta.wins,
                losses: meta.losses,
                score: meta.score,
            },
        });
        const done = sessionIdRef.current;
        sessionIdRef.current = null;
        deleteBlackjackSession(done);
    }, [play, recorder]);

    const initFromGm = useCallback(async () => {
        if (sessionIdRef.current) {
            await deleteBlackjackSession(sessionIdRef.current);
            sessionIdRef.current = null;
        }
        const data = await createBlackjackSession();
        if (!data?.session_id) throw new Error('Blackjack GM missing session_id');
        sessionIdRef.current = data.session_id;
        betRef.current = DEFAULT_BET;
        applyGm(data);
        recorder.reset({ bankroll: INITIAL_BANKROLL });
        // Match prior UX: deal first round immediately.
        const dealt = await moveBlackjackSession(data.session_id, {
            action: 'new_round',
            bet: DEFAULT_BET,
        });
        const meta = applyGm(dealt);
        if (dealt.round_outcome) {
            recorder.recordTick({
                result: dealt.round_outcome.result,
                payout: dealt.round_outcome.payout,
                player_hand: dealt.player_hand,
                dealer_hand: dealt.dealer_hand,
                bankroll: dealt.bankroll,
                bet: dealt.current_bet,
                score: dealt.score,
            });
        }
        maybeFinalize(meta);
    }, [applyGm, maybeFinalize, recorder]);

    const startGame = useCallback(async () => {
        try {
            setBusy(true);
            await initFromGm();
            play.startGame();
        } catch (err) {
            console.error(err);
            setMessage(gmStartErrorMessage(err));
        } finally {
            setBusy(false);
        }
    }, [initFromGm, play, setMessage]);

    const endGame = useCallback(async () => {
        const sid = sessionIdRef.current;
        sessionIdRef.current = null;
        await deleteBlackjackSession(sid);
        play.endGame({ score });
        setPlayerHand([]);
        setDealerHand([]);
        setBankroll(INITIAL_BANKROLL);
        setBet(DEFAULT_BET);
        betRef.current = DEFAULT_BET;
        setScore(0);
        setRoundActive(false);
        setTotalRounds(0);
        setIsGameOver(false);
        setWins(0);
        setLosses(0);
        setMessage('');
    }, [play, setMessage, score]);

    useEffect(() => () => {
        const sid = sessionIdRef.current;
        sessionIdRef.current = null;
        deleteBlackjackSession(sid);
    }, []);

    const step = useCallback(async (action) => {
        if (play.phase !== 'playing' || isGameOver || busy) return;
        const sid = sessionIdRef.current;
        if (!sid) return;
        setBusy(true);
        try {
            const data = await moveBlackjackSession(sid, { action, bet: betRef.current });
            const meta = applyGm(data);
            if (data.round_outcome && !data.round_active) {
                recorder.recordTick({
                    result: data.round_outcome.result,
                    payout: data.round_outcome.payout,
                    player_hand: data.player_hand,
                    dealer_hand: data.dealer_hand,
                    bankroll: data.bankroll,
                    bet: data.current_bet,
                    score: data.score,
                });
            }
            maybeFinalize(meta);
        } catch (err) {
            console.warn('Blackjack GM step failed', err);
            setMessage(err?.data?.message || 'Action failed');
        } finally {
            setBusy(false);
        }
    }, [applyGm, busy, isGameOver, maybeFinalize, play.phase, recorder, setMessage]);

    const onKey = useCallback((e) => {
        const k = e.key.toLowerCase();
        if (k === 'h') { step('hit'); e.preventDefault(); }
        else if (k === 's') { step('stand'); e.preventDefault(); }
        else if (k === 'd') { step('double'); e.preventDefault(); }
        else if (k === 'n') { step('new_round'); e.preventDefault(); }
        else if (e.key === '[') {
            betRef.current = Math.max(1, betRef.current - 5);
            setBet(betRef.current);
            e.preventDefault();
        } else if (e.key === ']') {
            betRef.current = Math.min(bankroll, betRef.current + 5);
            setBet(betRef.current);
            e.preventDefault();
        }
    }, [bankroll, step]);

    usePlayKeyboard({
        phase: play.phase,
        togglePause: play.togglePause,
        onKey,
        deps: [step, bankroll],
    });

    const playerValue = useMemo(() => (playerHand.length ? handValue(playerHand) : null), [playerHand]);
    const dealerValue = useMemo(() => (dealerHand.length ? handValue(dealerHand) : null), [dealerHand]);
    const shownDealer = roundActive && dealerHand.length ? [dealerHand[0], '??'] : dealerHand;

    return (
        <ArcadeGameShell
            title="Blackjack"
            titleAccent="Table"
            subtitle="Hit, stand, or double — play manually or upload an agent"
            accentColor={ACCENT}
            boilerplate={GAME_BOILERPLATE.blackjack}
            session={session}
            formatScore={(e) => String(e.score ?? e.bankroll ?? 0)}
        >
            <ManualPlayChrome
                phase={play.phase}
                result={play.result}
                onStart={startGame}
                onPause={play.togglePause}
                onEnd={endGame}
                accentColor={ACCENT}
                controlsHint={HINT}
            >
                <Box sx={{ p: 2 }}>
                    <Stack direction="row" spacing={1} sx={{ mb: 2, flexWrap: 'wrap' }}>
                        <Chip label={`Bankroll: ${bankroll}`} size="small" sx={{ color: ACCENT, borderColor: ACCENT }} variant="outlined" />
                        <Chip label={`Score: ${score}`} size="small" variant="outlined" sx={{ color: COLOR.textMuted, borderColor: COLOR.borderStrong }} />
                        <Chip label={`Bet: ${bet}`} size="small" variant="outlined" sx={{ color: COLOR.textMuted, borderColor: COLOR.borderStrong }} />
                        <Chip label={`Rounds: ${totalRounds}`} size="small" variant="outlined" sx={{ color: COLOR.textMuted, borderColor: COLOR.borderStrong }} />
                        <Chip label={`W/L: ${wins}/${losses}`} size="small" variant="outlined" sx={{ color: COLOR.textMuted, borderColor: COLOR.borderStrong }} />
                        <Chip label={roundActive ? 'Round active' : 'Between rounds'} size="small" variant="outlined" sx={{ color: COLOR.textMuted, borderColor: COLOR.borderStrong }} />
                    </Stack>
                    {message && <Typography sx={{ color: COLOR.textMuted, mb: 2, fontSize: '0.85rem' }}>{message}</Typography>}
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                        <Box sx={{ flex: 1, p: 2, bgcolor: 'rgba(0,0,0,0.35)', borderRadius: 2, border: `1px solid ${COLOR.border}` }}>
                            <Typography variant="subtitle2" sx={{ color: COLOR.textMuted, mb: 1 }}>
                                Dealer ({roundActive ? '?' : (dealerValue ?? '—')})
                            </Typography>
                            <Stack direction="row" spacing={1} flexWrap="wrap">
                                {shownDealer.map((card, idx) => <CardChip key={idx} card={card} />)}
                            </Stack>
                        </Box>
                        <Box sx={{ flex: 1, p: 2, bgcolor: 'rgba(0,0,0,0.35)', borderRadius: 2, border: `1px solid ${COLOR.border}` }}>
                            <Typography variant="subtitle2" sx={{ color: COLOR.textMuted, mb: 1 }}>Player ({playerValue ?? '—'})</Typography>
                            <Stack direction="row" spacing={1} flexWrap="wrap">
                                {playerHand.map((card, idx) => (
                                    <CardChip key={idx} card={card} highlight={playerValue === 21 && roundActive} />
                                ))}
                            </Stack>
                        </Box>
                    </Stack>
                </Box>
            </ManualPlayChrome>
        </ArcadeGameShell>
    );
};

export default Blackjack;
